import React, { useState } from 'react'
import { URL_THUMBOR } from 'constants/urls'
import { CURRENT_AWARENESS_ARTICLE } from 'constants/controllers'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import url from 'front-end-core/core/url'
import ArrowLink from 'components/redesign/ArrowLink'
import cx from 'classnames'
import Route from 'route-parser'
import { pushUrl } from 'front-end-core/core/history'
import { OverflowDetector } from 'react-overflow'
import { isLink } from 'helpers/dom'
import Skeleton from '@material-ui/lab/Skeleton'
import { htmlText } from 'helpers/htmlText'
import { parseHtmlContent } from 'helpers/parseHtmlContent'
import { useSelector } from 'react-redux'
import selectAolName from 'redux/selectors/selectAolName'

function pushUrlIfNotLink (URL) {
  return e => {
    if (!isLink(e.target)) {
      pushUrl(URL)
    }
  }
}

const thumborRoute = new Route(URL_THUMBOR)

const ReadFullStoryLink = ({ path, tileHover }) => {
  const aol = useSelector(selectAolName)
  const urlParams = { aol, id: path[1] }

  return (
    <div className='ml3'>
      <ArrowLink
        text='Read full story'
        url={url(CURRENT_AWARENESS_ARTICLE, { ...urlParams })}
        {...{ tileHover }}
      />
    </div>
  )
}

export const setContentLinksTargetAttribute = (content, value) => {
  const contentEl = (new DOMParser()).parseFromString(content, 'text/html').documentElement
  Array.prototype.slice.call(contentEl.querySelectorAll('a')).forEach((a) => {
    a.setAttribute('target', value)
    if (value === '_blank') {
      a.setAttribute('rel', 'noopener noreferrer')
    }
  })

  return contentEl
}

const Content = ({ headline = false, title, content, tileHover }) => {
  const contentEl = setContentLinksTargetAttribute(content, '_blank')
  const titleText = htmlText(title)

  return (
    <div className={cx('ca-content black ca-homepage', { mt2: headline }, { mt4: !headline })}>
      <div
        className={cx(
          'b ca-tile-title',
          { underline: tileHover },
          { 'lh-headline': headline },
          { 'lh-title': !headline }
        )}
        style={{
          fontSize: headline ? '1.75rem' : '1.125rem'
        }}
      >
        {titleText}
      </div>
      <div
        style={{
          fontSize: 15,
          marginTop: '1.25rem',
          lineHeight: '1.47'
        }}
      >
        {parseHtmlContent(contentEl.innerHTML)}
      </div>
    </div>
  )
}

const PublishedDate = ({ publishDate }) => (
  <p className='f6 cadet-blue mt0 mb3'>
    {publishDate}
  </p>
)

export const HeadlineStory = ({ imageUrl = false, publishDate, title, content, id, path }) => {
  const [heightOverflowing, setHeightOverflowing] = useState(false)
  const [tileHover, setHover] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const aol = useSelector(selectAolName)
  const urlParams = { aol, id: path[1] }
  const URL = url(CURRENT_AWARENESS_ARTICLE, { ...urlParams })

  return (
    <div
      className='ca-grid-card relative pt6 pb0 pl6 pr0 bg-white pointer'
      onClick={pushUrlIfNotLink(URL)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Grid container className='h-100 pb9 relative'>
        <Grid item sm={imageUrl ? 6 : 12} md={imageUrl ? 4 : 12} className='relative h-100'>
          <TileOverFlowDetector
            headline
            {...{ publishDate, imageUrl, title, content, id, path, tileHover, setHeightOverflowing }}
          />
          {
            heightOverflowing &&
              <div
                className={cx(
                  'absolute bottom0 tile-frosted-bottom w-100',
                  { h5: imageUrl },
                  { h5: !imageUrl })}
              />
          }
        </Grid>
        {imageUrl && (<HeadlineStoryImage {...{ imageUrl, imageLoaded, setImageLoaded }} />)}
        <Grid item sm={imageUrl ? 6 : 12} md={imageUrl ? 4 : 12} className='relative'>
          <div className='absolute bottom0 tile-frosted-bottom h3 w-100' />
        </Grid>
        {heightOverflowing &&
          <div className='absolute bottom6 left0'>
            <ReadFullStoryLink {...{ id, path, tileHover }} />
          </div>}
      </Grid>
    </div>
  )
}

const HeadlineStoryImage = ({ imageUrl, setImageLoaded, imageLoaded }) => {
  const src = thumborRoute.reverse({ url: imageUrl, size: '640x360' })

  return (
    <Grid item sm={6} md={8} className='pl6 h-100'>
      {!imageLoaded && (
        <Skeleton variant='rect' animation='pulse' height={377} classes={{ root: 'bg-void-grey' }} />
      )}
      {!imageLoaded && <img src={src} className='dn' onLoad={() => setImageLoaded(true)} />}
      {imageLoaded && (
        <div
          style={{ backgroundImage: `url(${src})`, height: '377px' }}
          className={cx('fit-bg-image')}
        />
      )}
    </Grid>
  )
}

const TileOverFlowDetector = ({ headline, publishDate, imageUrl, title, content, tileHover, setHeightOverflowing }) => (
  <OverflowDetector
    className='overflow-detector'
    onOverflowChange={(isHeightOverflowed) => setHeightOverflowing(isHeightOverflowed)}
    style={{
      height: headline ? 'inherit' : !imageUrl ? 360 : 360 - 175,
      overflow: 'hidden'
    }}
  >
    <Grid container direction='column' justify='space-between' className={cx('relative h-100')}>
      <Grid item className='max-w-100'>
        <PublishedDate {...{ publishDate }} />
        <Content {...{ headline, imageUrl, title, content, tileHover }} />
      </Grid>
    </Grid>
  </OverflowDetector>
)

export const Story = ({ imageUrl = false, publishDate, title, content, id, path, trimmed }) => {
  const [heightOverflowing, setHeightOverflowing] = useState(false)
  const [tileHover, setTileHover] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const aol = useSelector(selectAolName)
  const urlParams = { aol, id: path[1] }
  const URL = url(CURRENT_AWARENESS_ARTICLE, { ...urlParams })

  return (
    <div
      className='ca-grid-card relative pa6 bg-white pointer'
      onClick={pushUrlIfNotLink(URL)}
      onMouseEnter={() => setTileHover(true)}
      onMouseLeave={() => setTileHover(false)}
    >
      <Grid container direction='column' justify='space-between' className='h-100 relative'>
        <Grid item className='max-w-100'>
          <div className='relative'>
            <TileOverFlowDetector {...{ publishDate, imageUrl, title, content, id, path, tileHover, setHeightOverflowing }} />
            {heightOverflowing && (
              <div
                className={cx(
                  'absolute bottom0 tile-frosted-bottom w-100',
                  { h5: imageUrl },
                  { h5: !imageUrl })}
              />
            )}
          </div>
          {imageUrl && (<StoryImage {...{ imageUrl, setImageLoaded, imageLoaded }} />)}
        </Grid>
        <Hidden smUp>
          <div className='absolute bottom0'>
            <ReadFullStoryLink {...{ id, path, tileHover }} />
          </div>
        </Hidden>
        <Hidden xsDown>
          {(heightOverflowing || trimmed) &&
            <div className='absolute bottom0'>
              <ReadFullStoryLink {...{ id, path, tileHover }} />
            </div>}
        </Hidden>
      </Grid>
    </div>
  )
}

const StoryImage = ({ imageUrl, setImageLoaded, imageLoaded }) => {
  const src = thumborRoute.reverse({ url: imageUrl, size: '320x200' })

  return (
    <Grid item className='pb6 pt4' style={{ maxHeight: 175 }}>
      {!imageLoaded && (
        <Skeleton variant='rect' animation='pulse' height={154} classes={{ root: 'bg-void-grey' }} />
      )}
      {!imageLoaded && <img src={src} className='dn' onLoad={() => setImageLoaded(true)} />}
      {imageLoaded && (
        <div
          className='fit-bg-image'
          style={{
            backgroundImage: `url(${src})`,
            height: '154px'
          }}
        />
      )}
    </Grid>
  )
}
